import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus, House, MapPin, Users, Bed, LinkSimple, SpinnerGap, Sparkle, Lock, ArrowRight, X } from '@phosphor-icons/react';

const PROP_IMAGES = [
  "https://images.unsplash.com/photo-1770756051811-1612ac8bedfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBob21lJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc2MzkwMzg2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1774685110718-c5b4fe026144?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBob21lJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc2MzkwMzg2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1775989122161-0c55c459157a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBob21lJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc2MzkwMzg2fDA&ixlib=rb-4.1.0&q=85",
];

export default function PropertiesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [form, setForm] = useState({ name: '', address: '', property_type: 'apartment', bedrooms: 1, bathrooms: 1, max_guests: 2, description: '', nightly_rate: 100, image_url: '' });
  const [saving, setSaving] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  const runImport = async () => {
    setImportError('');
    if (!importUrl.trim()) { setImportError('Paste an Airbnb or Vrbo URL.'); return; }
    setImporting(true);
    try {
      const { data } = await api.post('/properties/import-from-url', { url: importUrl.trim() });
      // Pre-fill the form with whatever we parsed + fall back to defaults
      setForm(f => ({
        ...f,
        name: data.name || f.name,
        address: data.address || f.address,
        description: data.description || f.description,
        bedrooms: data.bedrooms || f.bedrooms,
        bathrooms: data.bathrooms || f.bathrooms,
        max_guests: data.max_guests || f.max_guests,
        property_type: data.property_type || f.property_type,
        nightly_rate: data.nightly_rate || f.nightly_rate,
        image_url: data.image_url || f.image_url,
      }));
      setShowImport(false);
      setImportUrl('');
      setShowForm(true);
    } catch (e) {
      setImportError(e?.response?.data?.detail || 'Import failed. Please fill in manually.');
    } finally {
      setImporting(false);
    }
  };

  const load = () => {
    api.get('/properties').then(r => setProperties(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/entitlements').then(r => setEntitlements(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Deep-link: /properties?import=1 auto-opens the import modal, then strips the param.
  useEffect(() => {
    if (searchParams.get('import') === '1' && entitlements) {
      if (entitlements.can_add_property) setShowImport(true);
      else setUpgradeMessage(
        entitlements.reason === 'trial_expired'
          ? 'Your 14-day free trial has ended. Subscribe to import properties.'
          : `Your free trial includes 1 property. Upgrade to add more.`
      );
      searchParams.delete('import');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, entitlements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/properties', form);
      setShowForm(false);
      setForm({ name: '', address: '', property_type: 'apartment', bedrooms: 1, bathrooms: 1, max_guests: 2, description: '', nightly_rate: 100, image_url: '' });
      load();
    } catch (err) {
      if (err?.response?.status === 402) {
        setShowForm(false);
        setUpgradeMessage(err.response.data?.detail || 'Upgrade to add more properties.');
      } else {
        alert(err?.response?.data?.detail || 'Error creating property');
      }
    } finally {
      setSaving(false);
    }
  };

  const tryAdd = (mode) => {
    if (entitlements && !entitlements.can_add_property) {
      let msg;
      if (entitlements.reason === 'trial_expired') msg = 'Your 14-day free trial has ended. Subscribe to add properties.';
      else if (entitlements.reason === 'free_trial') msg = `Your free trial includes 1 property. Upgrade to add more (${entitlements.trial_days_left} ${entitlements.trial_days_left === 1 ? 'day' : 'days'} left on trial).`;
      else msg = `Your current plan covers ${entitlements.property_limit} ${entitlements.property_limit === 1 ? 'property' : 'properties'}. Upgrade or increase your subscription quantity to add more.`;
      setUpgradeMessage(msg);
      return;
    }
    if (mode === 'import') setShowImport(true);
    else setShowForm(true);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="properties-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Properties</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your property listings and knowledge bases</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => tryAdd('manual')} className="px-4 py-2.5 border border-zinc-300 text-zinc-900 text-sm font-bold rounded-sm hover:bg-zinc-50 transition-colors flex items-center gap-2" data-testid="add-property-btn">
            <Plus weight="bold" size={14} /> Add manually
          </button>
          <button onClick={() => tryAdd('import')} className="px-4 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors flex items-center gap-2 shadow-sm" data-testid="import-property-btn">
            <Sparkle weight="fill" size={14} /> Import from Airbnb / Vrbo
          </button>
        </div>
      </div>

      {/* Add Form Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={() => !importing && setShowImport(false)} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-lg" data-testid="import-modal">
            <div className="p-6 border-b border-zinc-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle weight="fill" size={16} className="text-[#0052FF]" />
                <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Import from Airbnb or Vrbo</h2>
              </div>
              <p className="text-sm text-zinc-500">Paste a listing URL and we'll pre-fill the property details using AI. You can review &amp; edit before saving.</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Listing URL</label>
                <input
                  type="url"
                  value={importUrl}
                  onChange={e => setImportUrl(e.target.value)}
                  placeholder="https://www.airbnb.com/rooms/12345678"
                  className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm focus:border-[#0052FF] focus:outline-none"
                  data-testid="import-url-input"
                  autoFocus
                />
                <p className="text-[11px] text-zinc-400 mt-1.5">Works with Airbnb (/rooms/...) and Vrbo URLs.</p>
              </div>
              {importError && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs p-3 rounded-sm" data-testid="import-error">
                  {importError}
                </div>
              )}
              <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm text-xs text-zinc-600">
                <p className="font-bold text-zinc-900 mb-1">What we'll pre-fill:</p>
                <p>Property name, description, address, bedrooms, bathrooms, guest capacity, property type, nightly rate, and hero photo.</p>
                <p className="mt-2 text-[11px] text-zinc-500">You'll add WiFi password, check-in code, and any private details manually on the next screen.</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowImport(false)} disabled={importing} className="flex-1 py-2.5 border border-zinc-200 text-sm font-bold text-zinc-700 rounded-sm hover:bg-zinc-50" data-testid="import-cancel-btn">Cancel</button>
                <button onClick={runImport} disabled={importing} className="flex-1 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="import-submit-btn">
                  {importing ? <><SpinnerGap size={14} className="animate-spin" /> Analyzing listing…</> : <><LinkSimple size={14} weight="bold" /> Import & Pre-Fill</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowForm(false)} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="property-form-modal">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Add Property</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Property Name</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" placeholder="Beach House Malibu" data-testid="property-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Address</label>
                <input value={form.address} onChange={e => set('address', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" placeholder="123 Ocean Drive, Malibu CA" data-testid="property-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Type</label>
                  <select value={form.property_type} onChange={e => set('property_type', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm bg-white" data-testid="property-type-select">
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="condo">Condo</option>
                    <option value="villa">Villa</option>
                    <option value="cabin">Cabin</option>
                    <option value="studio">Studio</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Nightly Rate ($)</label>
                  <input type="number" value={form.nightly_rate} onChange={e => set('nightly_rate', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="property-rate-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Bedrooms</label>
                  <input type="number" min="0" value={form.bedrooms} onChange={e => set('bedrooms', parseInt(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="property-bedrooms-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Bathrooms</label>
                  <input type="number" min="0" value={form.bathrooms} onChange={e => set('bathrooms', parseInt(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="property-bathrooms-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Max Guests</label>
                  <input type="number" min="1" value={form.max_guests} onChange={e => set('max_guests', parseInt(e.target.value) || 1)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="property-guests-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm resize-none" placeholder="Beautiful beachfront property..." data-testid="property-description-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Property Image URL <span className="text-zinc-400 font-normal">(Optional)</span></label>
                <input value={form.image_url} onChange={e => set('image_url', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" placeholder="https://example.com/property-photo.jpg" data-testid="property-image-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-zinc-200 text-sm font-bold text-zinc-700 rounded-sm hover:bg-zinc-50 transition-colors" data-testid="property-cancel-btn">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors disabled:opacity-50" data-testid="property-save-btn">{saving ? 'Saving...' : 'Save Property'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Property List */}
      {loading ? (
        <p className="text-sm text-zinc-500">Loading...</p>
      ) : properties.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="no-properties">
          <House size={40} weight="duotone" className="text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No properties yet</h3>
          <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">Paste your existing Airbnb or Vrbo URL and we'll pre-fill everything in 10 seconds — or add manually.</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => tryAdd('import')} className="px-6 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] inline-flex items-center gap-2" data-testid="import-first-property-btn">
              <Sparkle weight="fill" size={14} /> Import from Airbnb / Vrbo
            </button>
            <button onClick={() => tryAdd('manual')} className="px-6 py-2.5 border border-zinc-300 text-zinc-900 text-sm font-bold rounded-sm hover:bg-zinc-50 inline-flex items-center gap-2" data-testid="add-first-property-btn">
              <Plus weight="bold" size={14} /> Add manually
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200 bg-white" data-testid="properties-grid">
          {properties.map((p, i) => (
            <div
              key={p.id}
              onClick={() => navigate(`/properties/${p.id}`)}
              className="border-b border-r border-zinc-200 cursor-pointer hover:bg-zinc-50 transition-colors"
              data-testid={`property-card-${i}`}
            >
              <img src={p.image_url || PROP_IMAGES[i % PROP_IMAGES.length]} alt={p.name} className="w-full h-40 object-cover" />
              <div className="p-4">
                <h3 className="text-sm font-bold text-zinc-950 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{p.name}</h3>
                <p className="text-xs text-zinc-500 flex items-center gap-1 mb-3"><MapPin size={12} /> {p.address}</p>
                <div className="flex items-center gap-4 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Bed size={12} /> {p.bedrooms} bed</span>
                  <span className="flex items-center gap-1"><Users size={12} /> {p.max_guests} guests</span>
                  <span className="font-bold text-zinc-900">${p.nightly_rate}/night</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upgrade modal — shown when user hits quota limit */}
      {upgradeMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="upgrade-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setUpgradeMessage('')} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-md p-6 shadow-2xl">
            <button onClick={() => setUpgradeMessage('')} className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-sm" data-testid="upgrade-modal-close">
              <X size={14} weight="bold" />
            </button>
            <div className="w-12 h-12 bg-[#0052FF] rounded-sm flex items-center justify-center mb-4">
              <Lock size={22} weight="fill" className="text-white" />
            </div>
            <h2 className="text-xl font-black text-zinc-950 tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Upgrade to add more properties</h2>
            <p className="text-sm text-zinc-600 leading-relaxed mb-5">{upgradeMessage}</p>
            <div className="bg-zinc-50 border border-zinc-100 rounded-sm p-4 mb-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">What you unlock</div>
              <ul className="space-y-1.5 text-xs text-zinc-700">
                <li className="flex items-start gap-2"><span className="text-[#0052FF] font-bold">✓</span> Unlimited properties (pay per property)</li>
                <li className="flex items-start gap-2"><span className="text-[#0052FF] font-bold">✓</span> AI voice concierge + SMS reminders</li>
                <li className="flex items-start gap-2"><span className="text-[#0052FF] font-bold">✓</span> Photo-verified cleaning &amp; auto-dispatch</li>
                <li className="flex items-start gap-2"><span className="text-[#0052FF] font-bold">✓</span> iCal sync across Airbnb, Vrbo, Booking.com</li>
              </ul>
            </div>
            <Link to="/billing" onClick={() => setUpgradeMessage('')} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD]" data-testid="upgrade-modal-cta">
              See plans <ArrowRight size={14} weight="bold" />
            </Link>
            <button onClick={() => setUpgradeMessage('')} className="w-full mt-2 px-5 py-2 text-sm font-medium text-zinc-500 hover:text-zinc-900" data-testid="upgrade-modal-later">
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
